import { Clock } from 'lucide-react'
import { copy } from '../../copy/investor-copy'
import { useRecentSearchesQuery } from '../../hooks/use-investor-assistant'
import { formatShortDate } from '../following/dates'

/** The day a search was made, as the prototype words it: Today, Yesterday, or a date. */
function dayOf(createdAt: string): string {
  const day = createdAt.slice(0, 10)
  if (day === new Date().toISOString().slice(0, 10)) return copy('search.recent.today')
  if (day === new Date(Date.now() - 86_400_000).toISOString().slice(0, 10)) {
    return copy('search.recent.yesterday')
  }
  return formatShortDate(createdAt)
}

/**
 * Under the search: the investor's last few searches, each one restored with its answer. The conversation open on the
 * page is not listed (each row is a conversation's latest search, so it is the row with the open search's id).
 */
export function RecentSearches({
  openSearchId,
  onPick,
}: {
  openSearchId?: string
  onPick: (searchId: string) => void
}) {
  const recent = useRecentSearchesQuery()
  const all = recent.data?.data.items ?? []
  const items = all.filter((item) => item.searchId !== openSearchId)
  // The only search is the one on screen: nothing else to list, and "Your searches appear here" would be wrong.
  if (items.length === 0 && all.length > 0) return null
  return (
    <section aria-label={copy('search.recent')}>
      <h2 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {copy('search.recent')}
      </h2>
      {items.length === 0 && (
        <p className="mt-2.5 text-sm text-muted-foreground">{copy('search.recent.empty')}</p>
      )}
      <ul className="mt-2.5 divide-y divide-border overflow-hidden rounded-xl border border-border bg-card/30">
        {items.map((item) => (
          <li key={item.searchId}>
            <button
              type="button"
              onClick={() => onPick(item.searchId)}
              className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-foreground/90 hover:bg-white/[0.04]"
            >
              <Clock className="size-3.5 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate">{item.text}</span>
              <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                {item.resultCount === 1
                  ? copy('search.recent.one')
                  : copy('search.recent.count', { n: item.resultCount })}
                {` · ${dayOf(item.createdAt)}`}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  )
}
